const CUSTOM_VARIABLES_BAD_WORDS = [
  "address",
  "businessname",
  "city",
  "country",
  "email",
  "emails",
  "firstname",
  "id",
  "lastname",
  "phone",
  "phonenumber",
  "phonenumbers",
  "source",
  "state",
  "status",
  "zip",
];

/**
 * Regular expression to validate custom variables.
 * A valid variable starts with a letter and can contain letters, numbers, spaces, and underscores.
 */
const VARIABLE_REGEX = /^\p{L}[ \p{L}\p{N}_]*$/u;

/**
 * Checks if a custom variable is valid by ensuring it does not match any
 * of the predefined bad words.
 *
 * @param {string} variable - The custom variable to validate.
 * @returns {boolean} - Returns true if the variable is valid, false otherwise.
 */
export function IsCustomVariableValid(variable: string): boolean {
  const normalizedVariable = variable.toLowerCase().replace(/[-_]/g, "");
  const normalizedBadWords = CUSTOM_VARIABLES_BAD_WORDS.map((word) =>
    word.replace(/[-_]/g, "")
  );
  return !normalizedBadWords.includes(normalizedVariable);
}

/**
 *
 * @param fields - An array of field names to check for allowed variables.
 * @returns {string[]} - An array of allowed variables, each trimmed to 50 characters and unique.
 */
export const GetVariablesAllowed = (fields: string[]): string[] => {
  //Use the regex to see which variables are allowed
  const allowedVariables = fields
    .map((f) => f.trim())
    .filter(
      (field) => VARIABLE_REGEX.test(field) && IsCustomVariableValid(field)
    );

  //Return the first 50 characters of the variable, trimmed, and unique
  return allowedVariables
    .map((v) => v.substring(0, 50).trim())
    .filter((v, i, a) => a.indexOf(v) === i);
};

/**
 * Detects and maps phone fields from CSV headers, supporting multiple languages
 * and numbered phone fields (e.g., phone_1, mobile_2, etc.)
 *
 * @param header - The CSV header to analyze
 * @param t - Translation function for the current language
 * @returns Object with phone type and index, or null if not a phone field
 */
export function DetectPhoneField(
  header: string,
  t: (key: string) => string
): {
  type: string;
  index: number;
  key: string;
} | null {
  const normalizedHeader = header.toLowerCase().trim();

  const workPatterns = [
    // English: treat work/office/business as work unless followed by cell/mobile/tel/telephone; capture trailing number anywhere later
    /^(?:.*?[ _-]*)?(?:work|office|business)(?:(?![ _-]*(?:cell|mobile|tel|telephone))[ _-]*[a-z]*)*[ _-]*(\d+)?$/i,
    // Spanish: trabajo/oficina/negocio as work unless followed by celular/movil/tel/telefono; capture trailing number
    /^(?:.*?[ _-]*)?(?:trabajo|oficina|negocio)(?:(?![ _-]*(?:cel|celular|movil|móvil|tel|telefono|teléfono))[ _-]*[a-z]*)*[ _-]*(\d+)?$/i,
    // Japanese
    /^(?:.*?[ _-]*)?(?:仕事|オフィス|ビジネス).*?(\d+)?$/i,
  ];

  const homePatterns = [
    // English: allow 'home', optional words like 'phone'/'number', then trailing digits
    /^(?:.*?[ _-]*)?home(?:[ _-]*[a-z]*)*[ _-]*(\d+)?$/i,
    // Spanish: 'casa'
    /^(?:.*?[ _-]*)?casa(?:[ _-]*[a-z]*)*[ _-]*(\d+)?$/i,
    // Japanese: 自宅
    /^(?:.*?[ _-]*)?(?:自宅).*?(\d+)?$/i,
  ];

  const faxPatterns = [
    // English: 'fax', allow 'number'/'phone', trailing digits
    /^(?:.*?[ _-]*)?fax(?:[ _-]*[a-z]*)*[ _-]*(\d+)?$/i,
  ];

  // Phone patterns (default)
  const phonePatterns = [
    // English: phone/mobile/cell/tel/telephone; trailing digits
    /^(?:.*?[ _-]*)?(?:phone|mobile|cell|tel|telephone)(?:[ _-]*[a-z]*)*[ _-]*(\d+)?$/i,
    // Spanish
    /^(?:.*?[ _-]*)?(?:teléfono|telefónico|telefonico|telefono|teléfonos|telefonos|móvil|movil|celular)(?:[ _-]*[a-z]*)*[ _-]*(\d+)?$/i,
    // Japanese
    /^(?:.*?[ _-]*)?(?:電話|携帯|携帯電話).*?(\d+)?$/i,
  ];

  // Check patterns in priority order: Work, Home, Fax, Phone
  const allPatterns = [
    { patterns: workPatterns, type: "work" },
    { patterns: homePatterns, type: "home" },
    { patterns: faxPatterns, type: "fax" },
    { patterns: phonePatterns, type: "phone" },
  ];

  for (const { patterns, type } of allPatterns) {
    for (const pattern of patterns) {
      const match = normalizedHeader.match(pattern);
      if (match) {
        const index = match[1] ? parseInt(match[1], 10) : 1;
        const key = `${type}_${index}`;
        return { type, index, key };
      }
    }
  }

  // Check if it matches any of the translation keys as fallback
  const translationKeys = [
    "phone",
    "mobile",
    "cell",
    "tel",
    "telephone",
    "fax",
    "work",
    "office",
    "home",
  ];

  for (const key of translationKeys) {
    const translatedValue = t(key).toLowerCase();
    if (
      normalizedHeader === translatedValue ||
      normalizedHeader.startsWith(translatedValue + "_")
    ) {
      const indexMatch = normalizedHeader.match(/_(\d+)$/);
      const index = indexMatch ? parseInt(indexMatch[1], 10) : 1;
      // Use translation function to determine phone type
      const phoneType =
        key === "phone" ||
        key === "mobile" ||
        key === "cell" ||
        key === "tel" ||
        key === "telephone"
          ? "phone"
          : key;
      const fieldKey = `${phoneType}_${index}`;
      return { type: phoneType, index, key: fieldKey };
    }
  }

  return null;
}

/**
 * Groups phone fields by type and creates appropriate field mappings
 *
 * @param headers - Array of CSV headers
 * @param t - Translation function for the current language
 * @returns Object with phone field mappings and remaining non-phone fields
 */
export function GroupPhoneFields(
  headers: string[],
  t: (key: string) => string
): {
  phoneFields: Array<{
    header: string;
    type: string;
    index: number;
    key: string;
  }>;
  nonPhoneFields: string[];
} {
  const phoneFields: Array<{
    header: string;
    type: string;
    index: number;
    key: string;
  }> = [];
  const nonPhoneFields: string[] = [];

  for (const header of headers) {
    const phoneField = DetectPhoneField(header, t);
    if (phoneField) {
      phoneFields.push({
        header,
        ...phoneField,
      });
    } else {
      nonPhoneFields.push(header);
    }
  }

  return { phoneFields, nonPhoneFields };
}
